import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaFileInvoice, FaUniversity, FaChartBar, FaDownload,
  FaArrowUp, FaArrowDown, FaCheckCircle, FaClock, FaTimesCircle,
  FaEye, FaEyeSlash, FaInfoCircle,
} from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area,
  PieChart, Pie, Cell, Legend,
} from 'recharts';
import { format, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import EditIbanModal from '../modals/EditIbanModal';
import api from '../../../services/api';

// ---- Helpers ----

const formatCurrency = (v) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);

const CHART_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6B7280'];

const getStatusBadge = (status) => {
  switch (status) {
    case 'paid': return { label: 'Payée', color: 'bg-green-100 text-green-700', icon: FaCheckCircle };
    case 'open': return { label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: FaClock };
    case 'draft': return { label: 'Brouillon', color: 'bg-gray-100 text-gray-600', icon: FaFileInvoice };
    case 'uncollectible': return { label: 'Irrécouvrable', color: 'bg-red-100 text-red-700', icon: FaTimesCircle };
    case 'void': return { label: 'Annulée', color: 'bg-gray-100 text-gray-500', icon: FaTimesCircle };
    default: return { label: status, color: 'bg-gray-100 text-gray-600', icon: FaFileInvoice };
  }
};

// ---- Component ----

const InvoicesPage = () => {
  const { currentUser, brand, stats, statsLoading } = useProContext();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState('overview');
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [monthlyData, setMonthlyData] = useState([]);
  const [chartLoading, setChartLoading] = useState(true);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showIbanModal, setShowIbanModal] = useState(false);

  // Fetch invoices
  const fetchInvoices = useCallback(async () => {
    if (!currentUser?.stripe_customer_id) return;
    setInvoicesLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await api.get(`/invoices/${currentUser.stripe_customer_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setInvoices(res.data?.data || []);
    } catch (err) {
      console.error('Erreur factures:', err);
    } finally {
      setInvoicesLoading(false);
    }
  }, [currentUser?.stripe_customer_id]);

  // Fetch 6-month revenue history
  const fetchMonthlyHistory = useCallback(async () => {
    setChartLoading(true);
    try {
      const token = localStorage.getItem('token');
      const now = new Date();
      const promises = Array.from({ length: 6 }, (_, i) => {
        const date = subMonths(now, 5 - i);
        const m = format(date, 'yyyy-MM');
        const label = format(date, 'MMM', { locale: fr });
        return api
          .get('/opticians/stats', { params: { month: m }, headers: { Authorization: `Bearer ${token}` } })
          .then((r) => ({
            month: label,
            revenue: r.data.monthlyRevenue || 0,
            ventes: r.data.monthlySales || 0,
            acceptes: r.data.successRate || 0,
            refuses: r.data.refusalRate || 0,
            enAttente: r.data.pendingRate || 0,
          }))
          .catch(() => ({ month: label, revenue: 0, ventes: 0, acceptes: 0, refuses: 0, enAttente: 0 }));
      });
      setMonthlyData(await Promise.all(promises));
    } catch {
      // ignore
    } finally {
      setChartLoading(false);
    }
  }, []);

  // Fetch payment method info
  const fetchPaymentMethod = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await api.get('/users/payment-method', {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentMethod(res.data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchInvoices();
    fetchMonthlyHistory();
    fetchPaymentMethod();
  }, [fetchInvoices, fetchMonthlyHistory, fetchPaymentMethod]);

  // Derived stats
  const invoiceStats = useMemo(() => {
    const paid = invoices.filter((i) => i.status === 'paid');
    const open = invoices.filter((i) => i.status === 'open');
    return {
      totalPaid: paid.reduce((s, i) => s + (i.amount_paid || 0), 0) / 100,
      totalOpen: open.reduce((s, i) => s + (i.amount_due || 0), 0) / 100,
      paidCount: paid.length,
      openCount: open.length,
      total: invoices.length,
    };
  }, [invoices]);

  const pieData = useMemo(() => {
    const last = monthlyData[monthlyData.length - 1];
    if (!last) return [];
    return [
      { name: 'Acceptés', value: last.acceptes },
      { name: 'En attente', value: last.enAttente },
      { name: 'Refusés', value: last.refuses },
    ].filter((d) => d.value > 0);
  }, [monthlyData]);

  const revenueChange = useMemo(() => {
    if (monthlyData.length < 2) return 0;
    const curr = monthlyData[monthlyData.length - 1]?.revenue || 0;
    const prev = monthlyData[monthlyData.length - 2]?.revenue || 0;
    return prev > 0 ? Math.round(((curr - prev) / prev) * 100) : 0;
  }, [monthlyData]);

  const tabs = [
    { key: 'overview', icon: FaChartBar, label: 'Vue d\'ensemble' },
    { key: 'invoices', icon: FaFileInvoice, label: 'Factures' },
    { key: 'iban', icon: FaUniversity, label: 'Coordonnées bancaires' },
  ];

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-white shadow-lg rounded-lg px-4 py-3 border border-gray-100">
        <p className="text-xs font-semibold text-gray-900 capitalize mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs text-gray-600">
            {p.name}: <span className="font-semibold" style={{ color: p.color }}>{p.name === 'revenue' ? formatCurrency(p.value) : p.value}</span>
          </p>
        ))}
      </div>
    );
  };

  return (
    <ProLayout title="Comptabilité">
      <div className="h-full flex flex-col">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-white px-5 gap-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-colors relative ${
                activeTab === tab.key ? 'text-gray-900' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {activeTab === tab.key && (
                <div className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{ backgroundColor: brand }} />
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {/* ============ VUE D'ENSEMBLE ============ */}
          {activeTab === 'overview' && (
            <div className="p-6 space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* CA du mois */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-xs text-gray-500 font-medium mb-1">CA du mois</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statsLoading ? '—' : formatCurrency(stats.monthlyRevenue || 0)}
                  </p>
                  {revenueChange !== 0 && (
                    <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${revenueChange > 0 ? 'text-green-600' : 'text-red-500'}`}>
                      {revenueChange > 0 ? <FaArrowUp className="w-2.5 h-2.5" /> : <FaArrowDown className="w-2.5 h-2.5" />}
                      {Math.abs(revenueChange)}% vs mois précédent
                    </div>
                  )}
                </div>

                {/* Ventes du mois */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-xs text-gray-500 font-medium mb-1">Ventes du mois</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statsLoading ? '—' : stats.monthlySales || 0}
                  </p>
                  <p className="text-xs text-gray-400 mt-2">
                    {statsLoading ? '' : `${stats.totalSales || 0} total`}
                  </p>
                </div>

                {/* Taux de conversion */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <p className="text-xs text-gray-500 font-medium mb-1">Taux d'acceptation</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {statsLoading ? '—' : `${Math.round(stats.clientAcceptanceRate || 0)}%`}
                  </p>
                  <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                    <span className="text-green-600">{stats.successRate || 0} acceptés</span>
                    <span className="text-red-500">{stats.refusalRate || 0} refusés</span>
                  </div>
                </div>
              </div>

              {/* Charts row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Revenue chart */}
                <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Chiffre d'affaires (6 derniers mois)</h3>
                  {chartLoading ? (
                    <div className="h-64 flex items-center justify-center">
                      <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: brand, borderTopColor: 'transparent' }} />
                    </div>
                  ) : (
                    <ResponsiveContainer width="100%" height={260}>
                      <AreaChart data={monthlyData}>
                        <defs>
                          <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={brand} stopOpacity={0.15} />
                            <stop offset="95%" stopColor={brand} stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                        <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}€`} />
                        <Tooltip content={<CustomTooltip />} />
                        <Area type="monotone" dataKey="revenue" name="CA" stroke={brand} strokeWidth={2.5} fill="url(#colorRevenue)" dot={{ r: 4, fill: brand }} />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>

                {/* Pie chart: répartition */}
                <div className="bg-white rounded-xl border border-gray-200 p-5">
                  <h3 className="text-sm font-semibold text-gray-900 mb-4">Répartition du mois</h3>
                  {pieData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="45%"
                          innerRadius={50}
                          outerRadius={80}
                          paddingAngle={4}
                          dataKey="value"
                        >
                          {pieData.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-400 text-sm">
                      Aucune donnée ce mois
                    </div>
                  )}
                </div>
              </div>

              {/* Ventes bar chart */}
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Ventes mensuelles</h3>
                {chartLoading ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: brand, borderTopColor: 'transparent' }} />
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={monthlyData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="acceptes" name="Acceptés" fill="#10B981" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="enAttente" name="En attente" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="refuses" name="Refusés" fill="#EF4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>

            </div>
          )}

          {/* ============ FACTURES ============ */}
          {activeTab === 'invoices' && (
            <div className="p-6 space-y-4">
              {/* Summary cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                    <FaCheckCircle className="w-5 h-5 text-green-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Payées</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(invoiceStats.totalPaid)}</p>
                    <p className="text-xs text-gray-400">{invoiceStats.paidCount} facture{invoiceStats.paidCount > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-yellow-50 rounded-lg flex items-center justify-center">
                    <FaClock className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">En attente</p>
                    <p className="text-lg font-bold text-gray-900">{formatCurrency(invoiceStats.totalOpen)}</p>
                    <p className="text-xs text-gray-400">{invoiceStats.openCount} facture{invoiceStats.openCount > 1 ? 's' : ''}</p>
                  </div>
                </div>
                <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4">
                  <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                    <FaFileInvoice className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Total factures</p>
                    <p className="text-lg font-bold text-gray-900">{invoiceStats.total}</p>
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {invoicesLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="w-10 h-10 border-2 rounded-full animate-spin" style={{ borderColor: brand, borderTopColor: 'transparent' }} />
                  </div>
                ) : invoices.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                      <FaFileInvoice className="w-6 h-6 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-900">Aucune facture</p>
                    <p className="text-xs text-gray-500 mt-1">Vos factures apparaîtront ici</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Numéro</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Montant</th>
                        <th className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Statut</th>
                        <th className="px-5 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {invoices.map((invoice) => {
                        const badge = getStatusBadge(invoice.status);
                        const StatusIcon = badge.icon;
                        return (
                          <tr key={invoice.id} className="hover:bg-gray-50 transition-colors">
                            <td className="px-5 py-4">
                              <span className="text-sm text-gray-900 capitalize">
                                {format(new Date(invoice.created * 1000), 'MMMM yyyy', { locale: fr })}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-sm font-medium text-gray-900">{invoice.number || '—'}</span>
                            </td>
                            <td className="px-5 py-4">
                              <span className="text-sm font-semibold text-gray-900">
                                {formatCurrency((invoice.status === 'paid' ? invoice.amount_paid : invoice.amount_due) / 100)}
                              </span>
                            </td>
                            <td className="px-5 py-4">
                              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${badge.color}`}>
                                <StatusIcon className="w-3 h-3" />
                                {badge.label}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-right">
                              {(invoice.hosted_invoice_url || invoice.invoice_pdf) && (
                                <button
                                  onClick={() => window.open(invoice.hosted_invoice_url || invoice.invoice_pdf, '_blank')}
                                  className="p-2 rounded-lg transition-colors hover:bg-gray-100"
                                  style={{ color: brand }}
                                  title="Télécharger"
                                >
                                  <FaDownload className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {/* ============ IBAN ============ */}
          {activeTab === 'iban' && (
            <div className="p-6 max-w-2xl mx-auto">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="px-6 py-5 border-b border-gray-100">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
                      <FaUniversity className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Coordonnées bancaires</h3>
                      <p className="text-sm text-gray-500">Gérez votre moyen de paiement SEPA</p>
                    </div>
                  </div>
                </div>

                {/* Current IBAN */}
                <div className="p-6 space-y-5">
                  {paymentMethod ? (
                    <div className="bg-gray-50 rounded-xl p-5">
                      <p className="text-xs text-gray-500 font-medium mb-2">IBAN actuel</p>
                      <p className="text-base font-mono font-medium text-gray-900 tracking-wider">
                        •••• •••• •••• •••• •••• {paymentMethod.last4}
                      </p>
                      {paymentMethod.bank_name && (
                        <p className="text-xs text-gray-400 mt-2">Banque : {paymentMethod.bank_name}</p>
                      )}
                    </div>
                  ) : (
                    <div className="bg-yellow-50 rounded-xl p-5 flex items-start gap-3">
                      <FaInfoCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-yellow-800">Aucun IBAN enregistré</p>
                        <p className="text-xs text-yellow-600 mt-1">Ajoutez un IBAN pour activer le prélèvement SEPA.</p>
                      </div>
                    </div>
                  )}

                  {/* Info box */}
                  <div className="rounded-xl p-4" style={{ backgroundColor: brand + '08' }}>
                    <h4 className="text-sm font-semibold mb-2" style={{ color: brand }}>Informations</h4>
                    <ul className="text-xs space-y-1" style={{ color: brand + 'CC' }}>
                      <li>• Le prélèvement SEPA sera effectué sur ce compte chaque mois</li>
                      <li>• La modification est prise en compte immédiatement</li>
                      <li>• Vos données bancaires sont sécurisées par Stripe</li>
                    </ul>
                  </div>

                  {/* CTA */}
                  <button
                    onClick={() => setShowIbanModal(true)}
                    className="w-full py-3 rounded-xl text-white font-semibold text-sm transition-opacity hover:opacity-90"
                    style={{ backgroundColor: brand }}
                  >
                    {paymentMethod ? 'Modifier mon IBAN' : 'Ajouter un IBAN'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit IBAN Modal */}
      <EditIbanModal
        isOpen={showIbanModal}
        onClose={() => setShowIbanModal(false)}
        currentUser={currentUser}
        onSave={() => fetchPaymentMethod()}
      />
    </ProLayout>
  );
};

export default InvoicesPage;
